import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { BrokerRoot } from '@components/routing/BrokerRoot';
import { ProtectedRoute } from '@components/routing/ProtectedRoute';
import { DashboardRoute } from '@components/routing/DashboardRoute';
import { SignupWizardLayout } from '@components/routing/SignupWizardLayout';
import { SignupSessionGuard } from '@components/routing/SignupSessionGuard';
import { BrokerRootRedirect, BrokerLangOnlyRedirect } from './routes/BrokerEntryRoutes';
import { AdminRouteGuard } from '@components/AdminRouteGuard';
import { AuthLoadingOverlay } from '@components/AuthLoadingOverlay';
import { LoadingBlock } from '@components/LoadingBlock';
import { BACKOFFICE_BASE, BACKOFFICE_LOGIN_PATH } from '@lib/backofficeRoutes';
import {
  RegisterRoute,
  SignUpOtpRoute,
  SignUpCompleteRoute,
  AuthNafathRoute,
  AuthAddressRoute,
  AuthSuccessRoute,
} from './routes/signup';
import {
  MainBrokerageRoute,
  SubBrokerageRoute,
  WalletRoute,
  BrokerSettingsRoute,
  NewBrokerageRoute,
  LottieRoute,
  AccessDeniedRoute,
  DealSlugRoute,
} from './routes/broker';

/** Eager imports — small surfaces that must not land in the broker or admin chunks. */
import { PayerFlow } from './pages/payer/PayerFlow';
import { PayDevRedirectPage } from './pages/PayDevRedirectPage';
import { PayShortRedirectPage } from './pages/PayShortRedirectPage';
import { BankSimulatorPage } from './pages/BankSimulatorPage';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminAccessDeniedPage } from './pages/admin/AdminAccessDeniedPage';
import { AdminLayout } from '@layouts/AdminLayout';
import { AdminOutletSuspenseFallback } from '@components/AdminOutletSuspenseFallback';
import { PageSkeleton } from '@components/PageSkeleton';
import { OverviewSkeleton, TablePageSkeleton } from '@components/skeletons';

/** Lazy admin pages — each gets its own chunk. */
const OverviewPage = lazy(() => import('@pages/admin/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const BrokersPage = lazy(() => import('@pages/admin/BrokersPage').then((m) => ({ default: m.BrokersPage })));
const DealsPage = lazy(() => import('@pages/admin/DealsPage').then((m) => ({ default: m.DealsPage })));
const AdminCreateDealWizardPage = lazy(() =>
  import('@pages/admin/AdminCreateDealWizardPage').then((m) => ({ default: m.AdminCreateDealWizardPage })),
);
const SettingsPage = lazy(() => import('@pages/admin/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AdminCreateBrokerPage = lazy(() =>
  import('@pages/admin/AdminCreateBrokerPage').then((m) => ({ default: m.AdminCreateBrokerPage })),
);
const AdminCreateAdminPage = lazy(() =>
  import('@pages/admin/AdminCreateAdminPage').then((m) => ({ default: m.AdminCreateAdminPage })),
);
const BrokerDetailPage = lazy(() => import('@pages/admin/BrokerDetailPage').then((m) => ({ default: m.BrokerDetailPage })));
const CommissionReportsPage = lazy(() =>
  import('@pages/admin/CommissionReportsPage').then((m) => ({ default: m.CommissionReportsPage })),
);
const DealDetailPage = lazy(() => import('@pages/admin/DealDetailPage').then((m) => ({ default: m.DealDetailPage })));
const SystemLogsPage = lazy(() => import('@pages/admin/SystemLogsPage').then((m) => ({ default: m.SystemLogsPage })));

const LoadingFallback = () => <LoadingBlock className="min-h-screen" />;

/**
 * Root layout: renders AuthLoadingOverlay (path-aware startup preloader) over all routes,
 * then the matched route via Outlet.
 */
function RootLayout() {
  return (
    <>
      <AuthLoadingOverlay />
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: BACKOFFICE_LOGIN_PATH, element: <AdminLoginPage /> },
      /*
       * Payer + dev tools come before /:lang so paths like /pay-dev and /bank-simulator
       * are never treated as a language code by BrokerRoot.
       */
      { path: '/pay/:dealId', element: <PayerFlow /> },
      { path: '/p/:code', element: <PayShortRedirectPage /> },
      { path: '/pay-dev', element: <PayDevRedirectPage /> },
      { path: '/bank-simulator', element: <BankSimulatorPage /> },
      /*
       * /backoffice/access-denied is a sibling of the /backoffice layout route (not a
       * child) so access-denied renders fullscreen without the admin sidebar.
       * Static path score 24 beats /:lang/access-denied score 17.
       */
      {
        path: `${BACKOFFICE_BASE}/access-denied`,
        element: (
          <AdminRouteGuard>
            <AdminAccessDeniedPage />
          </AdminRouteGuard>
        ),
      },
      /*
       * Admin layout route. All children are static paths so each scores 24+,
       * which beats the broker /:lang/X dynamic route score of 17. This prevents
       * /backoffice/settings from being matched by /:lang/settings.
       */
      {
        path: BACKOFFICE_BASE,
        element: (
          <AdminRouteGuard>
            <AdminLayout />
          </AdminRouteGuard>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><OverviewSkeleton /></AdminOutletSuspenseFallback>}>
                <OverviewPage />
              </Suspense>
            ),
          },
          {
            path: 'brokers',
            element: (
              <Suspense
                fallback={
                  <AdminOutletSuspenseFallback>
                    <TablePageSkeleton showPageHeading showPrimaryAction showSearch tableColumns={7} />
                  </AdminOutletSuspenseFallback>
                }
              >
                <BrokersPage />
              </Suspense>
            ),
          },
          {
            path: 'brokers/new',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <AdminCreateBrokerPage />
              </Suspense>
            ),
          },
          {
            path: 'brokers/:id',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <BrokerDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'brokerage',
            element: (
              <Suspense
                fallback={
                  <AdminOutletSuspenseFallback>
                    <TablePageSkeleton showPageHeading showPrimaryAction showSearch={false} tableColumns={7} />
                  </AdminOutletSuspenseFallback>
                }
              >
                <DealsPage />
              </Suspense>
            ),
          },
          { path: 'deals', element: <Navigate to={`${BACKOFFICE_BASE}/brokerage`} replace /> },
          {
            path: 'brokerage/new',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <AdminCreateDealWizardPage />
              </Suspense>
            ),
          },
          { path: 'deals/new', element: <Navigate to={`${BACKOFFICE_BASE}/brokerage/new`} replace /> },
          {
            path: 'brokerage/:id',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <DealDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'deals/:id',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <DealDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'commission-reports',
            element: (
              <Suspense
                fallback={
                  <AdminOutletSuspenseFallback>
                    <TablePageSkeleton showPageHeading showSecondaryAction filterSelectCount={3} tableColumns={5} />
                  </AdminOutletSuspenseFallback>
                }
              >
                <CommissionReportsPage />
              </Suspense>
            ),
          },
          {
            path: 'system-logs',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><TablePageSkeleton showPageHeading tableColumns={4} /></AdminOutletSuspenseFallback>}>
                <SystemLogsPage />
              </Suspense>
            ),
          },
          {
            path: 'settings',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <SettingsPage />
              </Suspense>
            ),
          },
          {
            path: 'settings/accounts/new',
            element: (
              <Suspense fallback={<AdminOutletSuspenseFallback><PageSkeleton /></AdminOutletSuspenseFallback>}>
                <AdminCreateAdminPage />
              </Suspense>
            ),
          },
          {
            path: 'settings/roles-permissions',
            element: <Navigate to={`${BACKOFFICE_BASE}/settings?tab=roles-permissions`} replace />,
          },
          { path: '*', element: <Navigate to={BACKOFFICE_BASE} replace /> },
        ],
      },
      { path: '/', element: <BrokerRootRedirect /> },
      {
        path: '/:lang',
        element: <BrokerRoot />,
        children: [
          { index: true, element: <BrokerLangOnlyRedirect /> },
          /* Signup wizard ————————————————————————————————————— */
          {
            element: <SignupWizardLayout />,
            children: [
              { path: 'signup', element: <RegisterRoute /> },
              { path: 'signup-verify', element: <SignUpOtpRoute /> },
              {
                path: 'signup-complete',
                element: <SignupSessionGuard><SignUpCompleteRoute /></SignupSessionGuard>,
              },
              {
                path: 'nafath-verification',
                element: <SignupSessionGuard><AuthNafathRoute /></SignupSessionGuard>,
              },
              {
                path: 'select-national-address',
                element: <SignupSessionGuard><AuthAddressRoute /></SignupSessionGuard>,
              },
              {
                path: 'success',
                element: <SignupSessionGuard><AuthSuccessRoute /></SignupSessionGuard>,
              },
            ],
          },
          /* Protected broker dashboard ————————————————————————— */
          {
            element: <ProtectedRoute />,
            children: [
              {
                element: <DashboardRoute />,
                children: [
                  { path: 'main-brokerage', element: <MainBrokerageRoute /> },
                  { path: 'sub-brokerage', element: <SubBrokerageRoute /> },
                  { path: 'wallet', element: <WalletRoute /> },
                  { path: 'settings', element: <BrokerSettingsRoute /> },
                  { path: 'new-brokerage', element: <NewBrokerageRoute /> },
                  { path: 'lottie', element: <LottieRoute /> },
                  { path: 'forbidden', element: <AccessDeniedRoute /> },
                  { path: 'access-denied', element: <AccessDeniedRoute /> },
                  /* Deal slug catch-all — must be last */
                  { path: ':slug', element: <DealSlugRoute /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <BrokerRootRedirect /> },
    ],
  },
]);
